import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import { Check, Loader2, ArrowLeft, Sparkles, HeartHandshake, Ticket, ShieldCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [places, setPlaces] = useState(null);
  const [rdvUnique, setRdvUnique] = useState(null);
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(null);
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null);
  const [waitStatus, setWaitStatus] = useState("none");
  const [mode, setMode] = useState("cycle");
  const [fullProgram, setFullProgram] = useState(null);
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  // Contexte de parcours (suivi + retour). Ne présélectionne AUCUNE offre : l'utilisateur choisit ici.
  const renewalCtx = sp.get("renewal") === "1";
  const returnTo = sp.get("return_to") || null;

  useEffect(() => {
    api.get("/plans").then((r) => { setPlans(r.data.plans); setPlaces(r.data.premium_places); setRdvUnique(r.data.rdv_unique); setExtras(r.data.extra_offers || []); });
    api.get("/waitlist/me").then((r) => setWaitStatus(r.data.status)).catch(() => {});
    api.get("/plans/full-program").then((r) => setFullProgram(r.data)).catch(() => {});
  }, []);

  const joinWaitlist = async () => {
    try {
      const r = await api.post("/waitlist/premium");
      setWaitStatus(r.data.status);
      toast.success("Tu es inscrit sur la liste d'attente. On te préviendra dès qu'une place se libère.");
    } catch { toast.error("Erreur"); }
  };

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const r = await api.post("/promo/validate", { code: promo, plan: "premium" });
      setApplied({ code: promo.trim().toUpperCase(), type: r.data.type, value: r.data.value, scope: r.data.scope || "both" });
      toast.success("Code promo appliqué");
    } catch (e) { setApplied(null); toast.error(e?.response?.data?.detail || "Code invalide"); }
  };

  const promoAppliesTo = (full) => {
    if (!applied) return false;
    const s = applied.scope || "both";
    return s === "both" || (full ? s === "full" : s === "cycle");
  };

  const priceWith = (base, full = false) => {
    if (!promoAppliesTo(full)) return base;
    return applied.type === "percent" ? Math.round(base * (1 - applied.value / 100) * 100) / 100 : Math.max(0, Math.round((base - applied.value) * 100) / 100);
  };

  const choose = async (key, full = false) => {
    setLoading(key);
    try {
      const r = await api.post("/payments/checkout", { plan: key, origin_url: window.location.origin, promo_code: (applied && promoAppliesTo(full)) ? applied.code : null, full_program: full, renewal: renewalCtx, return_to: returnTo });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de paiement"); setLoading(null); }
  };

  // Prestations SUR RENDEZ-VOUS : on choisit d'abord le créneau, puis on paie (tunnel dédié).
  const book = (key) => navigate(`/reserver?offer=${encodeURIComponent(key)}`);

  const Icon = { essentiel: Sparkles, premium: HeartHandshake };

  return (
    <ClientLayout>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-obsidian/50 hover:text-obsidian"><ArrowLeft size={15} /> Retour</button>
      <div className="fade-up mt-4">
        <ProvenanceBadge type="analyse">TON BILAN EST PRÊT</ProvenanceBadge>
        <h1 className="mt-5 text-3xl sm:text-4xl font-light tracking-tight">Choisis ton accompagnement</h1>
        <p className="mt-3 text-obsidian/60 leading-relaxed max-w-lg">Deux formules, un même moteur de programmation course à pied. Activation immédiate après paiement.</p>

        <div className="mt-6 inline-flex rounded-full border border-border bg-white p-1" data-testid="payment-mode-toggle">
          <button data-testid="mode-cycle" onClick={() => setMode("cycle")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-[background-color,color] ${mode === "cycle" ? "bg-obsidian text-white" : "text-obsidian/60 hover:text-obsidian"}`}>
            Payer par cycle
          </button>
          <button data-testid="mode-full" onClick={() => setMode("full")}
            className={`rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-[background-color,color] ${mode === "full" ? "bg-obsidian text-white" : "text-obsidian/60 hover:text-obsidian"}`}>
            Programme complet {fullProgram?.discount_pct > 0 && <span className={`rounded-full text-[10px] font-semibold px-1.5 py-0.5 ${mode === "full" ? "bg-sage text-white" : "bg-sage/15 text-sage"}`}>-{fullProgram.discount_pct}%</span>}
          </button>
        </div>

        {mode === "full" && fullProgram && (
          <div data-testid="full-program-info" className="mt-4 rounded-xl border border-sage/30 bg-sage/5 px-4 py-3 max-w-lg">
            <div className="text-sm font-medium text-obsidian">Programme recommandé : {fullProgram.cycles} cycle{fullProgram.cycles > 1 ? "s" : ""} · ~{fullProgram.weeks} semaines</div>
            <p className="mt-0.5 text-xs text-obsidian/60">
              {fullProgram.source === "race_date" ? "Estimé par l'IA d'après la date de ta course. " : "Estimé par l'IA d'après ton objectif. "}
              {fullProgram.discount_pct > 0
                ? `Paie tout en une fois et bénéficie de ${fullProgram.discount_pct} % de réduction sur le total (${fullProgram.cycles} cycles).`
                : "Paie tout en une fois. La réduction progressive s'applique à partir de 4 cycles."}
            </p>
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 max-w-sm" data-testid="promo-box">
          <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())}
            placeholder="Code promo" className="flex-1 rounded-full border border-border bg-white px-4 py-2.5 text-sm uppercase focus:ring-1 focus:ring-obsidian focus:outline-none" />
          <button data-testid="promo-apply" onClick={applyPromo} className="rounded-full bg-secondary px-4 py-2.5 text-sm font-medium hover:bg-obsidian hover:text-white transition-[background-color,color]">Appliquer</button>
        </div>
        {applied && (
          promoAppliesTo(mode === "full")
            ? <div className="mt-2 text-sm text-sage font-medium" data-testid="promo-applied">Code {applied.code} appliqué · -{applied.type === "percent" ? `${applied.value}%` : `${applied.value} €`}{mode === "full" && fullProgram?.discount_pct > 0 ? ` (cumulé avec le -${fullProgram.discount_pct}%)` : ""}</div>
            : <div className="mt-2 text-sm text-obsidian/50" data-testid="promo-not-applicable">Le code {applied.code} s'applique uniquement {applied.scope === "full" ? "au programme complet" : "à un paiement par cycle"}.</div>
        )}

        <div className="mt-8 grid md:grid-cols-2 gap-5 items-start">
          {plans.map((p) => {
            const PIcon = Icon[p.key] || Sparkles;
            const premium = p.key === "premium";
            const soldOut = premium && places !== null && places <= 0;
            const fp = mode === "full" && fullProgram ? fullProgram.options[p.key] : null;
            return (
              <div key={p.key} data-testid={`plan-${p.key}`}
                className={`relative rounded-[28px] border p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${premium ? "border-sage/40 ring-1 ring-sage/30 bg-obsidian text-white shadow-premium-hover md:scale-[1.03] md:hover:scale-[1.04]" : "border-border bg-white shadow-premium hover:shadow-premium-hover"}`}>
                <div className="flex items-center gap-2.5">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center ${premium ? "bg-white/10" : "bg-sage/15"}`}>
                    <PIcon size={17} className={premium ? "text-white" : "text-sage"} />
                  </span>
                  <span className="text-lg font-medium">{p.label}</span>
                </div>
                <p data-testid={`plan-tagline-${p.key}`} className={`mt-2 text-sm leading-snug ${premium ? "text-white/75" : "text-obsidian/65"}`}>
                  {premium
                    ? "Accompagnement IA + coach humain : ton programme est supervisé par un coach qui analyse tes demandes, reconstruit plusieurs semaines ou cycles, intègre un nouvel objectif et valide les changements importants."
                    : "Accompagnement 100 % IA : programme personnalisé, assistant MAI (explications, conseils) et ajustements ponctuels de séances. Sans coach humain — les refontes complètes relèvent de l'offre Premium."}
                </p>
                {premium && places !== null && (
                  <div data-testid="premium-places" className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium ${soldOut ? "bg-white/10 text-white/70" : "bg-sage/20 text-sage"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${soldOut ? "bg-white/50" : "bg-sage"}`} />
                    {soldOut ? "Toutes les places sont prises · patiente qu'une place se libère" : `${places} place${places > 1 ? "s" : ""} disponible${places > 1 ? "s" : ""}`}
                  </div>
                )}
                {fp ? (
                  <>
                    <div className="mt-5 flex items-baseline gap-1" data-testid={`full-price-${p.key}`}>
                      {fp.discount_pct > 0 && <span className={`font-display text-2xl font-light line-through ${premium ? "text-white/40" : "text-obsidian/30"}`}>{fp.total_before}</span>}
                      <span className="font-display text-5xl font-light">{priceWith(fp.total_after, true)} €</span>
                      {fp.discount_pct > 0 && <span className="ml-1 rounded-full bg-sage text-white text-[10px] font-semibold px-2 py-0.5">-{fp.discount_pct}%</span>}
                      {promoAppliesTo(true) && <span className="ml-1 rounded-full bg-sage text-white text-[10px] font-semibold px-2 py-0.5">PROMO</span>}
                    </div>
                    <div className={`mt-1 text-xs ${premium ? "text-white/50" : "text-obsidian/40"}`}>{fp.cycles} cycles · {fp.unit_price} € / cycle · payé en une fois{fp.savings > 0 ? ` · économise ${fp.savings} €` : ""}</div>
                  </>
                ) : (
                  <>
                    <div className="mt-5 flex items-baseline gap-1">
                      {(p.promo_price || promoAppliesTo(false)) && <span className={`font-display text-2xl font-light line-through ${premium ? "text-white/40" : "text-obsidian/30"}`}>{p.price}</span>}
                      <span className="font-display text-5xl font-light">{priceWith(p.promo_price || p.price, false)} €</span>
                      <span className={premium ? "text-white/50" : "text-obsidian/50"}>/ cycle</span>
                      {p.promo_price && <span className="ml-1 rounded-full bg-sage text-white text-[10px] font-semibold px-2 py-0.5">PROMO</span>}
                    </div>
                    <div className={`mt-1 text-xs ${premium ? "text-white/50" : "text-obsidian/40"}`}>Cycle de 4 semaines · sans engagement</div>
                  </>
                )}

                <ul className="mt-6 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <Check size={16} className={`mt-0.5 shrink-0 ${premium ? "text-sage" : "text-sage"}`} />
                      <span className={premium ? "text-white/85" : "text-obsidian/80"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button data-testid={`choose-${p.key}`} onClick={() => (soldOut ? joinWaitlist() : choose(p.key, mode === "full"))}
                  disabled={loading || (soldOut && (waitStatus === "waiting" || waitStatus === "notified"))}
                  className={`mt-7 w-full rounded-full py-3.5 font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:hover:translate-y-0 ${premium ? "bg-white text-obsidian" : "bg-obsidian text-white"}`}>
                  {loading === p.key ? <Loader2 size={16} className="animate-spin" /> : null}
                  {soldOut
                    ? ((waitStatus === "waiting" || waitStatus === "notified") ? "Inscrit sur la liste d'attente" : "Rejoindre la liste d'attente")
                    : (fp ? `Payer le programme · ${priceWith(fp.total_after, true)} €` : `Choisir ${p.label} · ${priceWith(p.promo_price || p.price, false)} €`)}
                </button>
                {soldOut && waitStatus === "none" && (
                  <p className={`mt-2 text-xs text-center ${premium ? "text-white/50" : "text-obsidian/40"}`}>On te prévient dès qu'une place se libère.</p>
                )}
              </div>
            );
          })}
        </div>

        {rdvUnique && (
          <div data-testid="rdv-unique-card" className="mt-5 rounded-2xl border border-border bg-white p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium"><Ticket size={17} className="text-sage" /> {rdvUnique.label} · {rdvUnique.price} €</div>
              <p className="mt-1 text-sm text-obsidian/60">{rdvUnique.description}</p>
            </div>
            <button data-testid="choose-rdv_unique" onClick={() => book("rdv_unique")} disabled={loading}
              className="rounded-full border border-obsidian px-6 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-obsidian hover:text-white transition-colors disabled:opacity-60 whitespace-nowrap">
              <CalendarClock size={15} /> Choisir un créneau · {rdvUnique.price} €
            </button>
          </div>
        )}

        {extras.map((o) => (
          <div key={o.key} data-testid={`extra-offer-${o.key}`} className="mt-3 rounded-2xl border border-border bg-white p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium"><Ticket size={17} className="text-sage" /> {o.label} · {o.price} €
                {o.requires_booking && <span className="inline-flex items-center gap-1 rounded-full bg-sage/15 text-sage text-[10px] font-semibold px-2 py-0.5"><CalendarClock size={11} /> Sur rendez-vous</span>}
              </div>
              {o.description && <p className="mt-1 text-sm text-obsidian/60">{o.description}</p>}
            </div>
            <button data-testid={`choose-${o.key}`} onClick={() => (o.requires_booking ? book(o.key) : choose(o.key))} disabled={loading}
              className="rounded-full border border-obsidian px-6 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-obsidian hover:text-white transition-colors disabled:opacity-60 whitespace-nowrap">
              {loading === o.key ? <Loader2 size={15} className="animate-spin" /> : (o.requires_booking ? <CalendarClock size={15} /> : null)} {o.requires_booking ? "Choisir un créneau" : "Choisir"} · {o.price} €
            </button>
          </div>
        ))}
        <p className="mt-6 text-xs text-obsidian/40 flex items-center justify-center gap-1.5"><ShieldCheck size={13} strokeWidth={1.6} /> Paiement 100% sécurisé par Stripe. Sans engagement, résiliable à tout moment.</p>
      </div>
    </ClientLayout>
  );
}
